import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';

import { useContinueRun, usePauseRun } from '@/generated/hooks/jeeves/v1/api';
import type { V1Run } from '@/generated/hooks/bishop/models';
import { captureException } from '@/modules/sentry';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import {
  ERunHeaderControlAction,
  getRunHeaderControlAction,
  type TRunHeaderControlAction,
} from '../utils/run-utils';

/**
 * Parameters for the useRunControl hook.
 */
export interface IUseRunControlParams {
  /** Automation (or draft) the run belongs to. */
  automationId: string;
  /** Run to pause/continue. Undefined disables the control entirely — used by
   *  surfaces where the focused run is optional (e.g. the editor's test-run
   *  banner before a run is focused). */
  runId: string | undefined;
  /** Current run state, used to decide between pause and continue. */
  runState: V1Run['state'];
  /** Reason recorded against the pause, naming the surface it came from.
   *  Defaults to the run page's, so a new surface that forgets to pass one
   *  doesn't silently claim to be the run page. */
  pauseReason?: string;
  /** Called after a successful pause/continue — not on error, hence
   *  `onSuccess` rather than React Query's settle-on-either `onSettled`.
   *  Surfaces that read the run from a one-shot query pass their refetch here;
   *  surfaces already fed by the bishop `run_update` stream can omit it. */
  onSuccess?: () => void;
}

/**
 * Return type for the useRunControl hook.
 */
export interface IUseRunControlReturn {
  /** Which action the control should offer, or null when no control applies —
   *  anything that isn't executing, stopping, or stopped, which includes
   *  `pending`, `awaiting_guidance`, `completed`, `failed`, `archived`, and an
   *  unset state. See `getRunHeaderControlAction` for the whitelist. */
  action: TRunHeaderControlAction;
  /** Whether a pause/continue request is in flight. */
  isPending: boolean;
  /** Whether a pause is in progress per the server: the `PauseRun` request in
   *  flight, or the run reporting `stopping` while it unwinds (long — the
   *  executor has to finish the step already in flight).
   *
   *  Note the seam between those two: the request resolves a beat before
   *  `stopping` reaches run state. Surfaces that care about presenting the
   *  pause as one continuous action bridge that gap locally, from the click
   *  (see `TestRunBanner`), rather than this hook guessing at it. */
  isPausing: boolean;
  /** Whether the control should be non-interactive — in flight, already
   *  transitioning through `stopping`, or missing the org/workspace/run
   *  context the request needs. */
  isDisabled: boolean;
  /** Runs `action` against the API. Resolves true when it succeeded, false
   *  when it failed or there was nothing to do — errors are handled in here
   *  (toast + Sentry) rather than thrown, so callers holding optimistic UI
   *  need this to know when to roll it back. */
  control: () => Promise<boolean>;
}

/**
 * Pause/continue control for a single run, shared by every surface that shows
 * a run executing — the run page header and the draft editor's live test-run
 * banner.
 *
 * "Stopping" a run is `PauseRun` (`runs/{run}:pause`): the run moves
 * `executing` → `stopping` → `stopped`, and `stopped` is resumable via
 * `ContinueRun`, which is why the same control flips to continue rather than
 * disappearing (see `getRunHeaderControlAction`).
 *
 * @param params - Automation/run identity, current run state, optional refetch
 * @returns The action to offer, its pending/disabled state, and the handler
 *
 * @example
 * ```tsx
 * const { action, isPending, isDisabled, control } = useRunControl({
 *   automationId,
 *   runId,
 *   runState: run?.state,
 *   onSuccess: refetchRun,
 * });
 * ```
 */
export function useRunControl({
  automationId,
  runId,
  runState,
  pauseReason,
  onSuccess,
}: IUseRunControlParams): IUseRunControlReturn {
  const { t: tAutomation } = useTranslation('automation');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const pauseRunMutation = usePauseRun();
  const continueRunMutation = useContinueRun();

  const action = getRunHeaderControlAction(runState);
  const isPending = pauseRunMutation.isPending || continueRunMutation.isPending;
  const isPausing = pauseRunMutation.isPending || Boolean(runState?.stopping);
  const isDisabled =
    isPending ||
    isPausing ||
    !runId ||
    !currentOrganizationId ||
    !currentWorkspaceId;

  const control = useCallback(async (): Promise<boolean> => {
    if (!action || !runId || !currentOrganizationId || !currentWorkspaceId) {
      return false;
    }

    try {
      if (action === ERunHeaderControlAction.PAUSE) {
        await pauseRunMutation.mutateAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          run: runId,
          data: {
            reason:
              pauseReason ?? tAutomation('automation.run.page.pauseReason'),
          },
        });
      } else {
        await continueRunMutation.mutateAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          run: runId,
          data: {},
        });
      }

      onSuccess?.();
      return true;
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error('Failed to control run'),
        {
          tags: {
            module: 'automation-run',
            action:
              action === ERunHeaderControlAction.PAUSE
                ? 'pause_run'
                : 'continue_run',
          },
        },
      );
      toast.error(
        tAutomation(
          action === ERunHeaderControlAction.PAUSE
            ? 'automation.run.page.header.pauseError'
            : 'automation.run.page.header.continueError',
        ),
      );
      return false;
    }
  }, [
    action,
    automationId,
    continueRunMutation,
    currentOrganizationId,
    currentWorkspaceId,
    onSuccess,
    pauseReason,
    pauseRunMutation,
    runId,
    tAutomation,
  ]);

  return { action, isPending, isPausing, isDisabled, control };
}
